/**
 * Offline grant verification — the device-side half of the ES256 grant ERPat
 * issues at provisioning (`specs/offline-attendance-v1/implementation-plan.md`,
 * decision D2 and the "Grant JWS" section of the shared wire contract).
 *
 * A grant is a compact JWS (`header.payload.signature`, base64url, no padding)
 * signed by a per-tenant P-256 key with ES256: ECDSA over SHA-256, the
 * signature being the raw 64-byte `r‖s` pair (RFC 7518 §3.4) — the form ERPat's
 * php-jwt writes and WebCrypto's ECDSA verifies directly. The public keys arrive
 * as a JWKS in the provisioning response.
 *
 * {@link verifyGrant} answers one question: may THIS device capture punches
 * offline under this token, for THIS account? In order, first failure wins:
 *
 *  1. `malformed`      — not three base64url segments, a header that is not a
 *                        JSON object with `alg: "ES256"` and a `kid`, a payload
 *                        that is not a JSON object, or a signature that is not
 *                        64 bytes;
 *  2. `unknown_kid`    — no usable key for the header's `kid`: none, several
 *                        (ambiguous), or one that is not a P-256 public key;
 *  3. `bad_signature`  — the signature does not verify (or WebCrypto could not
 *                        check it: fail closed);
 *  4. `claims`         — the payload fails the contract's `decodeGrantClaims()`,
 *                        or one of the four claims ERPat compares verbatim
 *                        (`gid`, `ins`, `uid`, `eid` — plan amendment C4) is not
 *                        already canonical;
 *  5. `binding`        — the grant is not for this scope: issuer pin, tenant,
 *                        user, employee, installation, grant id, and — when the
 *                        caller asks — policy version;
 *  6. `not_yet_valid` / `expired` — outside `vfr*1000 ≤ now ≤ vut*1000`, only
 *                        when `checkValidity` (the default; capture). Provisioning
 *                        and reload pass `false`: they verify authenticity, and
 *                        readiness judges the window with the corrected clock.
 *
 * Nothing in the payload is trusted before its signature verified: the header
 * is read to find the key, the payload is only parsed as JSON until then.
 *
 * Why the rules mirror ERPat exactly: a grant the device accepts and ERPat
 * refuses at upload turns every punch captured under it into a FINAL
 * `grant_invalid` that only HR can fix. ERPat (BE-3) compares
 * `gid === grant_id`, `uid === (string) user_id`, `eid === uid`,
 * `ins === installation_id` verbatim, `strtolower(tnt) === company_key` (PHP 8.2
 * `strtolower` folds ASCII only) and `iss === issuer`. So does this module —
 * independently of how far `decodeGrantClaims()` normalizes, which keeps the
 * refusal local even before the contract decoder is tightened (plan C4).
 *
 * Pure: no storage, no network, no clock but the one passed in (or
 * `Date.now()`), and it never throws — every outcome is a {@link GrantCheck}.
 */
import { decodeGrantClaims, type GrantClaimsV1 } from '@/contracts/offline-attendance-v1';

/** Why a grant was refused (see the module docblock for the order). */
export type GrantFailureReason =
  | 'malformed'
  | 'unknown_kid'
  | 'bad_signature'
  | 'claims'
  | 'binding'
  | 'not_yet_valid'
  | 'expired';

/**
 * The verdict. `detail` names WHAT failed, never a value: a token segment
 * (`token`, `header`, `header.alg`, `header.kid`, `payload`, `signature`), the
 * key problem (`kid`, `ambiguous`, `key`), `signature` / `unverifiable`, the
 * contract decoder's failing key or the canonical claim (`uid`, `acts[1]` …),
 * the bound claim (`iss`, `tnt`, `uid`, `eid`, `ins`, `gid`, `pv`), or the
 * window edge (`vfr`, `vut`, `now`). Diagnostics only — branch on `reason`.
 */
export type GrantCheck =
  | { ok: true; claims: GrantClaimsV1 }
  | { ok: false; reason: GrantFailureReason; detail?: string };

/** What the grant must be bound to — the scope that is asking. */
export interface GrantExpectation {
  /** The scope's normalized company key; compared case-insensitively (ASCII) with `tnt`. */
  companyKey: string;
  /** Decimal string; `uid` must equal it verbatim (and `eid` must equal `uid`). */
  userId: string;
  /** Lower-case uuid v4 of this installation; `ins` must equal it verbatim. */
  installationId: string;
  /** The provisioning response's `grant.grant_id` (decoded, lower-case); `gid` must equal it. */
  grantId: string;
  /** The issuer pinned by the scope's first provisioning; `null` accepts any (and the caller pins `claims.iss`). */
  issuer: string | null;
  /**
   * Optional: the policy version the grant must carry (`pv`). ERPat refuses an
   * event whose `policy_version` differs from its grant's `pv`, so a caller
   * that stamps events with the policy's version should pass it here.
   */
  policyVersion?: string | null;
}

/** A JWKS entry as the contract decodes it (`OfflineJwkV1`) or as stored (`JsonWebKey` with `kid`). */
export type GrantVerificationKey = JsonWebKey & { kid?: string };

/** Same ceiling as the contract decoder applies to `grant.token`. */
const MAX_TOKEN_LENGTH = 8_192;
/** One base64url segment: the URL-safe alphabet, no padding. */
const SEGMENT_RE = /^[A-Za-z0-9_-]+$/;
/** A base64url P-256 coordinate: 32 bytes → 43 unpadded characters. */
const COORDINATE_RE = /^[A-Za-z0-9_-]{43}$/;
/** ES256 carries `r‖s`, 32 bytes each. */
const ES256_SIGNATURE_BYTES = 64;
/** The claims ERPat compares verbatim — they must reach it already canonical. */
const VERBATIM_CLAIMS = ['gid', 'ins', 'uid', 'eid'] as const;

const ECDSA_P256: EcKeyImportParams = { name: 'ECDSA', namedCurve: 'P-256' };
const ES256: EcdsaParams = { name: 'ECDSA', hash: 'SHA-256' };

function refuse(reason: GrantFailureReason, detail: string): GrantCheck {
  return { ok: false, reason, detail };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** The bytes of one base64url segment, or `null` when it is not one. */
function base64UrlBytes(segment: string): Uint8Array | null {
  // A length of 4n+1 can never come from whole bytes.
  if (!SEGMENT_RE.test(segment) || segment.length % 4 === 1) return null;
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (segment.length % 4)) % 4);
  let binary: string;
  try {
    binary = atob(base64);
  } catch {
    // Unreachable for the alphabet checked above; refused all the same.
    return null;
  }
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return bytes;
}

/** A segment that holds a UTF-8 JSON object, parsed; `null` otherwise. */
function jsonObjectSegment(segment: string): Record<string, unknown> | null {
  const bytes = base64UrlBytes(segment);
  if (!bytes) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(bytes));
  } catch {
    // Invalid UTF-8 or not JSON: not a JWS segment this verifier understands.
    return null;
  }
  return isRecord(parsed) ? parsed : null;
}

/**
 * The public JWK WebCrypto imports for `key`, or `null` when `key` is not a
 * P-256 ES256 public key. Built fresh — `kid`, `alg`, `use`, `key_ops` never
 * reach `importKey()` — and a key carrying private material (`d`) is refused:
 * a key set that publishes it would let anyone mint grants.
 */
function publicJwk(key: GrantVerificationKey): JsonWebKey | null {
  if (key.kty !== 'EC' || key.crv !== 'P-256') return null;
  if (key.alg !== undefined && key.alg !== 'ES256') return null;
  if (key.d !== undefined) return null;
  if (typeof key.x !== 'string' || !COORDINATE_RE.test(key.x)) return null;
  if (typeof key.y !== 'string' || !COORDINATE_RE.test(key.y)) return null;
  return { kty: 'EC', crv: 'P-256', x: key.x, y: key.y };
}

/** ASCII-only lower case — what PHP 8.2's `strtolower()` does to `tnt` on ERPat. */
function asciiLower(value: string): string {
  return value.replace(/[A-Z]/g, (char) => String.fromCharCode(char.charCodeAt(0) + 32));
}

/** The first bound claim that does not match the scope, or `null` when the grant is ours. */
function bindingMismatch(claims: GrantClaimsV1, expected: GrantExpectation): string | null {
  if (expected.issuer !== null && claims.iss !== expected.issuer) return 'iss';
  if (asciiLower(claims.tnt) !== asciiLower(expected.companyKey)) return 'tnt';
  if (claims.uid !== expected.userId) return 'uid';
  if (claims.eid !== claims.uid) return 'eid';
  if (claims.ins !== expected.installationId) return 'ins';
  if (claims.gid !== expected.grantId) return 'gid';
  const policyVersion = expected.policyVersion;
  if (policyVersion !== undefined && policyVersion !== null && claims.pv !== policyVersion) return 'pv';
  return null;
}

/**
 * Verify an offline grant (see the module docblock).
 *
 * @param token          the compact JWS, as provisioning returned it.
 * @param keys           the provisioning JWKS (the key is chosen by the header's `kid`).
 * @param expected       the scope the grant must be bound to.
 * @param nowMs          the time to judge `vfr`/`vut` against — the CORRECTED clock
 *                       during capture; defaults to the device clock.
 * @param checkValidity  `false` checks authenticity and binding only (provisioning,
 *                       reload); the default `true` also requires the window.
 */
export async function verifyGrant(
  token: string,
  keys: readonly GrantVerificationKey[],
  expected: GrantExpectation,
  nowMs: number = Date.now(),
  checkValidity: boolean = true,
): Promise<GrantCheck> {
  /* 1. Structure */
  if (typeof token !== 'string' || token.length === 0 || token.length > MAX_TOKEN_LENGTH) {
    return refuse('malformed', 'token');
  }
  const parts = token.split('.');
  const [headerSegment, payloadSegment, signatureSegment] = parts;
  if (parts.length !== 3 || !headerSegment || !payloadSegment || !signatureSegment) {
    return refuse('malformed', 'token');
  }

  const header = jsonObjectSegment(headerSegment);
  if (!header) return refuse('malformed', 'header');
  if (header.alg !== 'ES256') return refuse('malformed', 'header.alg');
  const kid = header.kid;
  if (typeof kid !== 'string' || kid === '') return refuse('malformed', 'header.kid');

  const payload = jsonObjectSegment(payloadSegment);
  if (!payload) return refuse('malformed', 'payload');

  const signature = base64UrlBytes(signatureSegment);
  if (!signature || signature.length !== ES256_SIGNATURE_BYTES) return refuse('malformed', 'signature');

  /* 2. Key */
  const candidates = (Array.isArray(keys) ? keys : []).filter(
    (key): key is GrantVerificationKey => isRecord(key) && key.kid === kid,
  );
  const [candidate] = candidates;
  if (!candidate) return refuse('unknown_kid', 'kid');
  if (candidates.length > 1) return refuse('unknown_kid', 'ambiguous');
  const jwk = publicJwk(candidate);
  if (!jwk) return refuse('unknown_kid', 'key');

  /* 3. Signature */
  const subtle: SubtleCrypto | undefined = (globalThis as { crypto?: Crypto }).crypto?.subtle;
  if (!subtle) return refuse('bad_signature', 'unverifiable');
  let verificationKey: CryptoKey;
  try {
    verificationKey = await subtle.importKey('jwk', jwk, ECDSA_P256, false, ['verify']);
  } catch {
    // Not a point on P-256 (or a key the engine refuses): there is no usable key.
    return refuse('unknown_kid', 'key');
  }
  let verified: boolean;
  try {
    verified = await subtle.verify(
      ES256,
      verificationKey,
      signature,
      new TextEncoder().encode(`${headerSegment}.${payloadSegment}`),
    );
  } catch {
    // The engine could not check it at all: never read that as "verified".
    return refuse('bad_signature', 'unverifiable');
  }
  if (!verified) return refuse('bad_signature', 'signature');

  /* 4. Claims */
  const decoded = decodeGrantClaims(payload);
  if (!decoded.ok) return refuse('claims', decoded.reason);
  const claims = decoded.value;
  for (const name of VERBATIM_CLAIMS) {
    // The decoder may have normalized it; ERPat will not.
    if (payload[name] !== claims[name]) return refuse('claims', name);
  }

  /* 5. Binding */
  const mismatch = bindingMismatch(claims, expected);
  if (mismatch) return refuse('binding', mismatch);

  /* 6. Validity window */
  if (checkValidity) {
    if (!Number.isFinite(nowMs)) return refuse('expired', 'now');
    if (nowMs < claims.vfr * 1000) return refuse('not_yet_valid', 'vfr');
    if (nowMs > claims.vut * 1000) return refuse('expired', 'vut');
  }

  return { ok: true, claims };
}
